// ChemistryTracker.js
// Mirrors PickUpChemistryTracker.swift exactly.
// Horizontally scrollable row of TeamPillCards — one per city with 2+ picks.
// Each card: city name (top-left), year pills (glow when same city+year 2+),
// vertical separator, dot column (filled = playerCount, max 3).
// Collapses to nothing when no players drafted yet.

import React, { useRef } from "react";
import FitText from "./FitText";

function buildChemistryEntries(players) {
  const cityMap = {};
  players.forEach(p => {
    if (!cityMap[p.city]) cityMap[p.city] = [];
    cityMap[p.city].push(String(p.year));
  });

  return Object.entries(cityMap)
    .map(([city, years]) => {
      const yearCounts = {};
      years.forEach(y => { yearCounts[y] = (yearCounts[y] || 0) + 1; });
      const chemYears = new Set(
        Object.entries(yearCounts).filter(([, c]) => c >= 2).map(([y]) => y)
      );
      return { city, years: [...years].sort(), chemYears, playerCount: years.length };
    })
    .sort((a, b) => b.playerCount - a.playerCount || a.city.localeCompare(b.city));
}

export default function ChemistryTracker({ players, sport, headerRight = null }) {
  const entries = buildChemistryEntries(players);

  // Drag-to-scroll for desktop/web (mouse). Touch already scrolls natively via overflowX.
  // Hooks must run before any early return, so they sit above the empty-state guard.
  const scrollRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });

  const onMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.pageX, startScroll: el.scrollLeft };
  };
  const onMouseMove = (e) => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    el.scrollLeft = drag.current.startScroll - (e.pageX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  // When a header toggle is present (Pick Up YOU/OPP), keep the box rendered even with no
  // chemistry so the toggle stays reachable. Otherwise collapse to nothing when empty.
  if (entries.length === 0 && !headerRight) return null;

  return (
    <div style={{ ...styles.container, borderColor: `${sport.hex}1f` }}>
      <div style={styles.header}>
        <span style={styles.headerLabel}>CHEMISTRY TRACKER</span>
        {headerRight && <div style={styles.headerRight}>{headerRight}</div>}
      </div>

      {/* Horizontally scrollable — hide scrollbar visually; drag-to-scroll on desktop */}
      <div
        ref={scrollRef}
        className="chem-scroll"
        style={styles.scrollRow}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        {entries.length === 0 ? (
          <div style={styles.emptyHint}>No chemistry yet</div>
        ) : (
          entries.map((entry, i) => (
            <TeamPillCard key={i} entry={entry} sport={sport} />
          ))
        )}
      </div>

      <style>{`
        .chem-scroll::-webkit-scrollbar { display: none; }
        .chem-scroll { cursor: grab; }
        .chem-scroll:active { cursor: grabbing; }
      `}</style>
    </div>
  );
}

function TeamPillCard({ entry, sport }) {
  const MAX_DOTS = 3;
  return (
    <div style={{ ...styles.pillCard, borderColor: `${sport.hex}59` }}>
      {/* Left: city + year pills */}
      <div style={styles.pillLeft}>
        <FitText text={entry.city} style={styles.cityName} minScale={0.7} />
        <div style={styles.yearPillsRow}>
          {entry.years.map((year, i) => {
            const isChem = entry.chemYears.has(year);
            return (
              <span key={i} style={{
                ...styles.yearPill,
                background: isChem ? `${sport.hex}33` : "rgba(255,255,255,0.08)",
                borderColor: isChem ? `${sport.hex}99` : "rgba(255,255,255,0.12)",
                color: isChem ? sport.hex : "rgba(255,255,255,0.6)",
                boxShadow: isChem ? `0 0 6px ${sport.hex}66` : "none",
              }}>
                {year}
              </span>
            );
          })}
        </div>
      </div>

      {/* Vertical separator */}
      <div style={styles.separator} />

      {/* Right: dot column */}
      <div style={styles.dotColumn}>
        {Array.from({ length: MAX_DOTS }).map((_, i) => (
          <div key={i} style={{
            ...styles.dot,
            background: i < entry.playerCount ? sport.hex : "rgba(255,255,255,0.15)",
          }} />
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: "14px",
    border: "1px solid",
    padding: "7px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  headerLabel: {
    fontSize: "12px",
    fontWeight: "800",
    color: "#ffffff",
    letterSpacing: "1.6px",
  },
  // Slot for the optional YOU/OPP toggle (Pick Up). Sits inside the box at top-right and is
  // sized so it never grows the header row beyond the label's line height.
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  emptyHint: {
    fontSize: "11px",
    color: "rgba(255,255,255,0.3)",
    padding: "6px 2px",
  },
  scrollRow: {
    display: "flex",
    gap: "6px",
    overflowX: "auto",
    paddingBottom: "2px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    userSelect: "none",
  },
  pillCard: {
    width: "110px",
    height: "74px",
    flexShrink: 0,
    background: "rgba(255,255,255,0.04)",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    overflow: "hidden",
  },
  pillLeft: {
    flex: 1,
    padding: "6px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    overflow: "hidden",
    minWidth: 0,
  },
  cityName: {
    fontSize: "9px",
    fontWeight: "800",
    color: "#ffffff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: "0.3px",
  },
  yearPillsRow: {
    display: "grid",
    // Two fixed columns so pills fill into a 2-wide grid (4 years → 2x2)
    // instead of stacking in a single tall column.
    gridTemplateColumns: "repeat(2, auto)",
    justifyContent: "start",
    justifyItems: "start",
    alignContent: "start",
    gap: "3px",
  },
  yearPill: {
    fontSize: "9px",
    fontWeight: "700",
    padding: "2px 5px",
    borderRadius: "4px",
    border: "1px solid",
    letterSpacing: "0.2px",
    transition: "all 0.2s ease",
  },
  separator: {
    width: "1px",
    background: "rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  dotColumn: {
    // Narrowed from 24px → 14px so the separator sits right beside the dots,
    // handing the extra width to the city/year column on the left.
    width: "14px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    transition: "background 0.2s ease",
  },
};
